import { useEffect, useState } from 'react';
import { WatchlistApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { handleAdminError } from '../../common/SharedFunctions';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { formatDateMonthDayYear, formatCurrency, formatPercent } from '@apply/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import LoadPanel from 'devextreme-react/load-panel';

const DrawFundingHistoryTable = ({ loanId }) => {
    const [data, set_data] = useState([]);
    const [loadingMessage, set_loadingMessage] = useState('');
    const watchlistApi = new WatchlistApi.WatchlistApi(applicationInsights);

    const formatDate = (dt) => {
        if (!dt) {
            return null;
        }
        return formatDateMonthDayYear(dt);
    };

    const onDateRender = (e) => {
        return formatDate(e.value);
    };

    const loadHistoryAsync = async (loanId) => {
        try {
            set_loadingMessage('Retrieving history...');
            const response = await watchlistApi.getDrawFundingHistory(loanId);
            if (response.statusCode !== 200) {
                throw new Error('Failed to retrieve data!');
            }
            const records = response.result;
            set_data(records);
        } catch (ex) {
            handleAdminError(ex, 'Failed to retrieve records! Please refresh the page and try again.');
        } finally {
            set_loadingMessage('');
        }
    };

    useEffect(() => {
        if (!loanId) {
            return;
        }

        loadHistoryAsync(loanId);
    }, [loanId]);

    const onCurrencyRender = (e) => {
        return formatCurrency(e.value, 2);
    };

    const onDrawAmountRender = (e) => {
        if (!e.value) {
            return null;
        }
        return (
            <span>
                {formatCurrency(e.value, 2)} ({formatPercent(e.data.drawPercent)})
            </span>
        );
    };

    const onInspectionRender = (e) => {
        if (!e.value) {
            return null;
        }
        if (e.data.inspectionUrl) {
            return (
                <a
                    href={e.data.inspectionUrl}
                    target='_blank'
                    rel='noreferrer'
                >
                    {formatDate(e.value)}{' '}
                    <FontAwesomeIcon
                        icon={faDownload}
                        style={{ color: 'mediumblue', cursor: 'pointer' }}
                        title='Click to download file'
                    />
                </a>
            );
        } else {
            return (
                <span>
                    {formatDate(e.value)}{' '}
                    <FontAwesomeIcon
                        icon={faDownload}
                        style={{ color: 'lightgrey' }}
                        title='File not available for download'
                    />
                </span>
            );
        }
    };

    if (!loanId) {
        return null;
    }

    return (
        <>
            <DataGrid
                keyExpr={'id'}
                dataSource={data}
                rowAlternationEnabled={true}
                showBorders={true}
                showColumnHeaders={true}
                showRowLines={true}
                wordWrapEnabled={true}
                noDataText={'No maturity history found for this loan'}
            >
                <Column
                    dataField='fundedDate'
                    caption='Funded Date'
                    width={100}
                    cellRender={onDateRender}
                />
                <Column
                    dataField='drawAmount'
                    caption='Draw Amount'
                    width={180}
                    cellRender={onDrawAmountRender}
                    alignment='left'
                />
                <Column
                    dataField='newBalance'
                    caption='New Balance'
                    width={130}
                    cellRender={onCurrencyRender}
                    alignment='left'
                />
                <Column
                    dataField='inspectionDate'
                    caption='Inspection'
                    cellRender={onInspectionRender}
                />
            </DataGrid>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};

export default DrawFundingHistoryTable;
